import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, getDocs, query, where, getDoc, doc } from 'firebase/firestore';
import { db } from '../../services/firebaseConnection';
import Header from '../../components/Header';
import Title from '../../components/Title';
import { FaTasks } from 'react-icons/fa';

export default function UserTasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [customLocalNames, setCustomLocalNames] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const reportsPerPage = 7;

  const cityCache = {};
  const churchCache = {};

  const getCityName = async (cityId) => {
    if (cityCache[cityId]) return cityCache[cityId];
    const cityDoc = await getDoc(doc(db, 'Cidade', cityId));
    cityCache[cityId] = cityDoc.exists() ? cityDoc.data().nome : 'Cidade não encontrada';
    return cityCache[cityId];
  };

  const getChurchName = async (churchId) => {
    if (churchCache[churchId]) return churchCache[churchId];
    const churchDoc = await getDoc(doc(db, 'Igreja', churchId));
    churchCache[churchId] = churchDoc.exists() ? churchDoc.data().nome : 'Igreja não encontrada';
    return churchCache[churchId];
  };

  const fetchCustomLocalNames = async (churchIds) => {
    const customNames = {};
    const fetchPromises = churchIds.map(async (churchId) => {
      const churchDoc = await getDoc(doc(db, 'Igreja', churchId));
      if (churchDoc.exists()) {
        customNames[churchId] = churchDoc.data();
      }
    });
    await Promise.all(fetchPromises);
    setCustomLocalNames(customNames);
  };

  const fetchTasksForUser = useCallback(async (user) => {
    if (!user) {
      console.error('Usuário não carregado corretamente do localStorage.');
      setLoading(false);
      return;
    }

    try {
      const reportsQuery = query(
        collection(db, 'Reports'),
        where('ativo', '==', true)
      );
      const reportsSnapshot = await getDocs(reportsQuery);

      if (reportsSnapshot.empty) {
        setTasks([]);
        setLoading(false);
        return;
      }

      const userTasks = [];
      const currentDate = new Date();
      currentDate.setHours(currentDate.getHours() - 3);
      const churchIds = new Set();

      for (const reportDoc of reportsSnapshot.docs) {
        const reportData = reportDoc.data();
        const cityName = await getCityName(reportData.cidade);
        const churchName = await getChurchName(reportData.igreja);
        churchIds.add(reportData.igreja);

        if (Array.isArray(reportData.report)) {
          for (const entry of reportData.report) {
            Object.keys(customLocalNames[reportData.igreja] || {}).forEach((local) => {
              if (Array.isArray(entry[local])) {
                entry[local].forEach((person) => {
                  if (person.id === user.uid) {
                    const taskDate = entry.date.seconds
                      ? new Date(entry.date.seconds * 1000)
                      : new Date(entry.date);
                      
                    if (taskDate >= currentDate) {
                      userTasks.push({
                        cidade: cityName,
                        igreja: churchName,
                        date: taskDate,
                        local: customLocalNames[reportData.igreja]?.[local] || local,
                      });
                    }
                  }
                });
              }
            });
          }
        }
      }

      await fetchCustomLocalNames([...churchIds]);

      userTasks.sort((a, b) => a.date - b.date);
      setTasks(userTasks);
    } catch (error) {
      console.error('Erro ao buscar relatórios:', error);
    } finally {
      setLoading(false);
    }
  }, [customLocalNames]);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('@userLogado'));
    if (storedUser) {
      setUser(storedUser);
      fetchTasksForUser(storedUser);
    } else {
      console.error('Nenhum usuário encontrado no localStorage.');
      setLoading(false);
    }
  }, [fetchTasksForUser]);

  const adjustTimezone = (date) => {
    if (!date) return { date: 'Data não disponível', time: 'Hora não disponível' };
    const newDate = new Date(date);
    newDate.setHours(newDate.getHours() + 3);
    return {
      date: newDate.toLocaleString('pt-BR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
      time: newDate.toLocaleString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    };
  };

  const sortedTasks = useMemo(() => (tasks.length > 0 ? [...tasks].sort((a, b) => a.date - b.date) : []), [tasks]);

  const indexOfLastReport = currentPage * reportsPerPage;
  const indexOfFirstReport = indexOfLastReport - reportsPerPage;
  const currentReports = sortedTasks.slice(indexOfFirstReport, indexOfLastReport);

  if (loading) {
    return <div>Carregando...</div>;
  }

  return (
    <div>
      <Header />
      <div className="content">
        <Title name="Meus Compromissos">
          <FaTasks size={25} />
        </Title>
        <div className="container">
          {sortedTasks.length === 0 ? (
            <div>Você não tem compromissos esse mês.</div>
          ) : (
            <ul>
              {currentReports.map((task, index) => {
                const { date, time } = adjustTimezone(task.date);
                return (
                  <li key={index} className="card">
                    <p><strong>Cidade:</strong> {task.cidade}</p>
                    <p><strong>Casa de Oração:</strong> {task.igreja}</p>
                    <p><strong>Data:</strong> {date}</p>
                    <p><strong>Hora:</strong> {time}</p>
                    <p className="local"><strong>Local:</strong> {task.local}</p>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {sortedTasks.length === 0 ? (
          <></>
        ) : (
          <>
            <div className="pagination">
              <button
                onClick={() => {
                  setCurrentPage(currentPage - 1);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                disabled={currentPage === 1}
                className="button_pag"
              >
                Anterior
              </button>

              <span>Página {currentPage} de {Math.ceil(sortedTasks.length / reportsPerPage)}</span>

              <button
                onClick={() => {
                  setCurrentPage(currentPage + 1);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                disabled={indexOfLastReport >= sortedTasks.length}
                className="button_pag"
              >
                Próxima
              </button>
            </div>
          </>
        )}

      </div>
    </div>
  );
}
